import 'twin.macro';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import Button from 'components/Buttons/Button';
import ButtonExternal from 'components/Buttons/ExternalLink';
import useNotification from 'containers/App/Notifications/useNotification';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import useSchoolyears from 'queries/schoolyears/useSchoolyears';
import { trackEvent } from 'queries/user/track';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import Cookies from 'js-cookie';
import { atomWithSessionStorage } from 'utils/atomWithSessionStorage';

const skipBookmarkWarningAtom = atomWithSessionStorage<boolean | undefined>(
  'bookmarkToPastYear.skipWarning',
  undefined,
);
export const useSetSkipBookmarkWarning = () =>
  useSetAtom(skipBookmarkWarningAtom);
const isPermanentlyIgnored = !!Cookies.get('bookmarkToPastYear.ignoreWarning');

export function useWarnBookmarkToPastYear() {
  const { notifyWarning, close: closeWarning } = useNotification();
  const { schoolyear } = useSchoolSchoolyear();
  const { data: allSchoolyears = [], isSuccess } = useSchoolyears();
  const setSkipBookmarkWarning = useSetSkipBookmarkWarning();
  const skipBookmarkWarning = useAtomValue(skipBookmarkWarningAtom);
  const isLatestSchoolyear = allSchoolyears.every(
    sy => sy.year <= schoolyear.year,
  );
  const { t } = useTranslation(['common']);

  useEffect(() => {
    if (
      isSuccess &&
      !isLatestSchoolyear &&
      !skipBookmarkWarning &&
      !isPermanentlyIgnored
    ) {
      // we don't want to warn the user again if he navigates from Year X to and back to Year Y
      // so we store a boolean in the session storage
      setSkipBookmarkWarning(true);

      trackEvent('BookmarkToPastYear:notify');

      notifyWarning({
        title: t('common:messages.warning'),
        message: (
          <Trans
            i18nKey="common:bookmarkToPastYear.message"
            values={{ schoolyearLabel: schoolyear.label }}
          >
            __A__
            <strong>2022-2023</strong>
            __B__
          </Trans>
        ),
        actions: (
          <div tw="flex gap-x-2 justify-end">
            <Button
              minimal
              intent="warning"
              onClick={() => {
                Cookies.set('bookmarkToPastYear.ignoreWarning', true, {
                  expires: 30, // Expires in 30 days
                });
                trackEvent('BookmarkToPastYear:ignore');
                closeWarning();
              }}
            >
              {t('common:bookmarkToPastYear.ignore')}
            </Button>
            <ButtonExternal
              intent="warning"
              href="https://teetsh.com/documentation/guide/preparer-rentree/#a-chaque-fois-que-je-me-connecte-sur-teetsh-je-tombe-sur-mon-annee-scolaire-precedente"
              onClick={() => {
                trackEvent('BookmarkToPastYear:modify');
              }}
              icon={QuestionMarkCircleIcon}
            >
              {t('common:bookmarkToPastYear.modifyShortcut')}
            </ButtonExternal>
          </div>
        ),
        layout: 'block',
        timeout: 30000,
      });
    }
  }, [isSuccess, isLatestSchoolyear, skipBookmarkWarning]);
}
